import { useEffect, useMemo, useState } from 'react'
import { AppShell } from '../layout/AppShell'
import { Icon } from '../components/Icon'
import {
  ModuleExportModal,
  ModuleRecordModal,
  type ModuleModalMode,
} from '../components/ModuleRecordModal'
import {
  ModuleSettingsModal,
  type ModulePreferences,
  type ModuleSettingsMode,
} from '../components/ModuleSettingsModal'
import { MODULE_CATALOG } from '../data/moduleCatalog'

type Props = {
  moduleId: string
  currentPath: string
}

type ModalState = {
  mode: ModuleModalMode
  index?: number
}

const DEFAULT_PREFERENCES: ModulePreferences = {
  pageSize: '25',
  density: 'Comfortable',
  autoRefresh: false,
  orientation: 'Landscape',
  paperSize: 'Letter',
  includeSummary: true,
}

export function ModuleStubPage({ moduleId, currentPath }: Props) {
  const module = MODULE_CATALOG[moduleId] || MODULE_CATALOG.reports
  const [activeSection, setActiveSection] = useState(0)
  const [activeFeature, setActiveFeature] = useState(module.sections[0]?.items[0] || '')
  const [query, setQuery] = useState('')
  const [recordQuery, setRecordQuery] = useState('')
  const [status, setStatus] = useState('All statuses')
  const [rows, setRows] = useState<string[][]>(() => module.rows.map((row) => [...row]))
  const [modal, setModal] = useState<ModalState | null>(null)
  const [exportData, setExportData] = useState<{ filename: string; csv: string } | null>(null)
  const [settingsMode, setSettingsMode] = useState<ModuleSettingsMode | null>(null)
  const [visibleColumns, setVisibleColumns] = useState<boolean[]>(() => module.columns.map(() => true))
  const [preferences, setPreferences] = useState<ModulePreferences>({ ...DEFAULT_PREFERENCES })
  const [notice, setNotice] = useState('')

  useEffect(() => {
    setActiveSection(0)
    setActiveFeature(module.sections[0]?.items[0] || '')
    setQuery('')
    setRecordQuery('')
    setStatus('All statuses')
    setRows(module.rows.map((row) => [...row]))
    setModal(null)
    setExportData(null)
    setSettingsMode(null)
    try {
      const saved = localStorage.getItem(`madc.module-settings.${module.id}`)
      const parsed = saved ? JSON.parse(saved) : null
      setVisibleColumns(
        Array.isArray(parsed?.visibleColumns) && parsed.visibleColumns.length === module.columns.length
          ? parsed.visibleColumns
          : module.columns.map(() => true),
      )
      setPreferences({ ...DEFAULT_PREFERENCES, ...(parsed?.preferences || {}) })
    } catch {
      setVisibleColumns(module.columns.map(() => true))
      setPreferences({ ...DEFAULT_PREFERENCES })
    }
  }, [moduleId])

  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase()
    const section = module.sections[activeSection] || module.sections[0]
    return section.items.filter((item) => !q || item.toLowerCase().includes(q))
  }, [moduleId, activeSection, query])

  const visibleRows = useMemo(() => {
    const q = recordQuery.trim().toLowerCase()
    return rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => {
        if (q && !row.some((cell) => cell.toLowerCase().includes(q))) return false
        if (status !== 'All statuses' && row[row.length - 1] !== status) return false
        return true
      })
  }, [rows, recordQuery, status])

  const chooseSection = (index: number) => {
    setActiveSection(index)
    setActiveFeature(module.sections[index]?.items[0] || '')
    setQuery('')
  }

  const showNotice = (message: string) => {
    setNotice(message)
    window.setTimeout(() => setNotice(''), 2400)
  }

  const saveRow = (nextRow: string[]) => {
    if (modal?.mode === 'edit' && modal.index != null) {
      setRows((current) => current.map((row, index) => index === modal.index ? nextRow : row))
      showNotice(`${activeFeature} updated.`)
    } else {
      setRows((current) => [nextRow, ...current])
      showNotice(`${activeFeature} added.`)
    }
    setModal(null)
  }

  const deleteRow = () => {
    if (modal?.index == null) return
    setRows((current) => current.filter((_, index) => index !== modal.index))
    setModal(null)
    showNotice(`${activeFeature} deleted.`)
  }

  const exportCsv = () => {
    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`
    const csv = [
      module.columns.map(escape).join(','),
      ...visibleRows.map(({ row }) => row.map(escape).join(',')),
    ].join('\n')
    const filename = `${module.id}-${activeFeature.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`
    setExportData({ filename, csv })
  }

  const saveSettings = (nextColumns: boolean[], nextPreferences: ModulePreferences) => {
    try {
      localStorage.setItem(
        `madc.module-settings.${module.id}`,
        JSON.stringify({ visibleColumns: nextColumns, preferences: nextPreferences }),
      )
    } catch {
      // The UI remains usable when storage is unavailable.
    }
  }

  const saveColumns = (nextColumns: boolean[]) => {
    setVisibleColumns(nextColumns)
    saveSettings(nextColumns, preferences)
    showNotice('Column settings saved.')
  }

  const savePreferences = (nextPreferences: ModulePreferences) => {
    setPreferences(nextPreferences)
    saveSettings(visibleColumns, nextPreferences)
    showNotice('View settings saved.')
  }

  const activeRow = modal?.index != null ? rows[modal.index] : undefined

  return (
    <AppShell
      currentPath={currentPath}
      title={module.title}
      subtitle={module.subtitle}
      search={{
        value: query,
        onInput: setQuery,
        placeholder: `Search ${module.title.toLowerCase()} features…`,
      }}
      actions={
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => setModal({ mode: 'create' })}
        >
          <Icon name="plus" /> {module.action}
        </button>
      }
    >
      <div className={`container module-page module-page--${module.id}`}>
        <section className="module-hero">
          <div className="module-hero__icon">
            <Icon name={module.icon} />
          </div>
          <div>
            <span>{module.title} module</span>
            <h2>{module.title} workspace</h2>
            <p>{module.subtitle}</p>
          </div>
          <div className="module-hero__badge">
            <span className="module-hero__pulse" />
            Live workspace
          </div>
        </section>

        <section className="module-metrics" aria-label={`${module.title} summary`}>
          {module.metrics.map((metric, index) => (
            <article className={`module-metric module-metric--${index + 1}`} key={metric.label}>
              <span className="module-metric__icon">
                <Icon name={index === 0 ? module.icon : index === 1 ? 'clock' : index === 2 ? 'chart' : 'bell'} />
              </span>
              <div>
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
                <small>{metric.hint}</small>
              </div>
            </article>
          ))}
        </section>

        <section className="module-toolbar" aria-label={`${module.title} tools`}>
          <div className="module-toolbar__label">
            <Icon name="admin" />
            <span>Tools</span>
          </div>
          <button type="button" onClick={() => setSettingsMode('view')}>
            <Icon name="admin" /> Settings
          </button>
          <button type="button" onClick={() => setSettingsMode('columns')}>
            <Icon name="grid" /> Column Settings
          </button>
          <button type="button" onClick={() => setSettingsMode('print')}>
            <Icon name="reports" /> Print Settings
          </button>
          <button type="button" onClick={() => setSettingsMode('print-preview')}>
            <Icon name="reports" /> Print
          </button>
          <button type="button" onClick={exportCsv}>
            <Icon name="reports" /> Export Excel
          </button>
        </section>

        <section className="module-workspace">
          <aside className="module-catalog">
            <div className="module-catalog__head">
              <span className="module-catalog__icon"><Icon name={module.icon} /></span>
              <div>
                <b>{module.title}</b>
                <small>{module.sections.length} groups</small>
              </div>
            </div>

            <div className="module-section-tabs">
              {module.sections.map((section, index) => (
                <button
                  type="button"
                  key={section.label}
                  className={index === activeSection ? 'is-active' : ''}
                  onClick={() => chooseSection(index)}
                >
                  {section.label}
                  <span>{section.items.length}</span>
                </button>
              ))}
            </div>

            <div className="module-feature-list">
              {visibleItems.map((item) => (
                <button
                  type="button"
                  key={item}
                  className={item === activeFeature ? 'is-active' : ''}
                  onClick={() => setActiveFeature(item)}
                >
                  <Icon name="chevron-right" size={15} />
                  <span>{item}</span>
                </button>
              ))}
              {!visibleItems.length ? <p>No features match your search.</p> : null}
            </div>
          </aside>

          <div className="module-main">
            <div className="module-main__head">
              <div>
                <span className="eyebrow">{module.sections[activeSection]?.label}</span>
                <h2>{activeFeature || module.title}</h2>
              </div>
              <div className="module-filter-row">
                <label>
                  <span>Find records</span>
                  <input
                    type="search"
                    aria-label="Find records"
                    value={recordQuery}
                    placeholder="Search table…"
                    onInput={(event) => setRecordQuery((event.target as HTMLInputElement).value)}
                  />
                </label>
                <label>
                  <span>Status</span>
                  <select
                    aria-label="Filter status"
                    value={status}
                    onChange={(event) => setStatus((event.target as HTMLSelectElement).value)}
                  >
                    <option>All statuses</option>
                    <option>Open</option>
                    <option>Ready</option>
                    <option>In progress</option>
                    <option>Complete</option>
                    <option>Needs review</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="table-wrap module-table">
              <table className="data-table">
                <thead>
                  <tr>
                    {module.columns.map((column, index) => visibleColumns[index] ? <th key={column}>{column}</th> : null)}
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(({ row, index }) => (
                    <tr key={`${row[0]}-${index}`}>
                      {row.map((cell, cellIndex) => visibleColumns[cellIndex] ? (
                        <td className={cellIndex > 1 ? 'muted' : ''} key={`${cell}-${cellIndex}`}>
                          {cellIndex === row.length - 1 ? (
                            <span className={`module-status module-status--${cell.toLowerCase().replace(/\s+/g, '-')}`}>
                              {cell}
                            </span>
                          ) : cell}
                        </td>
                      ) : null)}
                      <td>
                        <div className="cell-actions">
                          <button
                            type="button"
                            className="act-btn"
                            title="View details"
                            onClick={() => setModal({ mode: 'view', index })}
                          >
                            <Icon name="eye" />
                          </button>
                          <button
                            type="button"
                            className="act-btn"
                            title="Edit"
                            onClick={() => setModal({ mode: 'edit', index })}
                          >
                            <Icon name="edit" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!visibleRows.length ? (
                <div className="empty">
                  <div><Icon name="search" /></div>
                  <p>No records match the current filters.</p>
                </div>
              ) : null}
              <div className="list-foot">
                <span>Showing {visibleRows.length} of {rows.length} records</span>
                <span>Local demonstration data</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {modal ? (
        <ModuleRecordModal
          mode={modal.mode}
          moduleTitle={module.title}
          featureTitle={activeFeature || module.title}
          columns={module.columns}
          row={activeRow}
          onClose={() => setModal(null)}
          onEdit={() => setModal((current) => current ? { ...current, mode: 'edit' } : null)}
          onSave={saveRow}
          onDelete={modal.mode === 'edit' ? deleteRow : undefined}
        />
      ) : null}

      {exportData ? (
        <ModuleExportModal
          moduleTitle={module.title}
          filename={exportData.filename}
          csv={exportData.csv}
          onClose={() => setExportData(null)}
        />
      ) : null}

      {settingsMode ? (
        <ModuleSettingsModal
          mode={settingsMode}
          moduleTitle={module.title}
          featureTitle={activeFeature || module.title}
          columns={module.columns}
          visibleColumns={visibleColumns}
          preferences={preferences}
          rows={visibleRows.map(({ row }) => row)}
          onClose={() => setSettingsMode(null)}
          onSaveColumns={saveColumns}
          onSavePreferences={savePreferences}
        />
      ) : null}

      {notice ? <div className="module-toast" role="status"><Icon name="check" /> {notice}</div> : null}
    </AppShell>
  )
}
